import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiCallWithHost } from '../../utils/apiClient'

// Helper to get current language
const getCurrentLanguage = () => {
  return window.i18n?.language || window.i18next?.language || document.documentElement.lang || 'en'
}

// Helper to load exercises from localStorage
const loadFromLocalStorage = (language) => {
  try {
    const storageKey = `exercises_${language}`
    const persistedExercises = localStorage.getItem(storageKey)

    if (persistedExercises) {
      const parsed = JSON.parse(persistedExercises)
      const isStale = Date.now() - (parsed.timestamp || 0) > 24 * 60 * 60 * 1000 // 24 hours

      if (!isStale && parsed.language === language) {
        console.log(
          `📦 exercisesSlice: Loaded ${
            parsed.exercises?.length || 0
          } exercises from localStorage for: ${language}`,
        )
        return {
          exercises: parsed.exercises || [],
          referenceData: parsed.referenceData || {},
        }
      } else {
        console.log(`⚠️ exercisesSlice: Stale data for ${language}, will fetch fresh`)
        localStorage.removeItem(storageKey)
      }
    }
    return null
  } catch (error) {
    console.error('Error loading exercises from localStorage:', error)
    return null
  }
}

// Initialize exercises from localStorage (call this on app start or component mount)
export const initializeExercises = createAsyncThunk(
  'exercises/initializeExercises',
  async (_, { getState, dispatch }) => {
    const state = getState()
    const currentLanguage = getCurrentLanguage()

    // If we already have exercises loaded, skip
    if (state.exercises.exercises.length > 0) {
      console.log('📦 exercisesSlice: Exercises already loaded, skipping initialization')
      return { skipped: true }
    }

    // Try to load from localStorage
    const cached = loadFromLocalStorage(currentLanguage)
    if (cached) {
      return { cached, language: currentLanguage }
    }

    return { cached: null, language: currentLanguage }
  },
)

// Fetch multiple exercises by ID - only fetches ones not already in Redux
export const fetchMissingExercises = createAsyncThunk(
  'exercises/fetchMissingExercises',
  async ({ token, exerciseIds }, { getState }) => {
    const state = getState()
    const activeProfile = state.user.activeProfile
    const existingExercises = state.exercises.exercises

    // Filter to only IDs not already in Redux
    const missingIds = exerciseIds.filter(
      (id) => !existingExercises.find((ex) => ex.id === id || ex.id === parseInt(id)),
    )

    if (missingIds.length === 0) {
      console.log('📦 exercisesSlice: All requested exercises already in Redux')
      return []
    }

    console.log(`🔍 exercisesSlice: Fetching ${missingIds.length} missing exercises:`, missingIds)

    // Fetch each missing exercise
    const fetchedExercises = []
    for (const exerciseId of missingIds) {
      try {
        const response = await apiCallWithHost(
          `/v2/exercises/${exerciseId}`,
          {},
          token,
          activeProfile?.id,
        )
        if (response.ok) {
          const exercise = await response.json()
          fetchedExercises.push(exercise)
        }
      } catch (error) {
        console.error(`Failed to fetch exercise ${exerciseId}:`, error)
      }
    }

    return fetchedExercises
  },
)

// Async thunks
export const fetchReferenceData = createAsyncThunk(
  'exercises/fetchReferenceData',
  async (token, { getState }) => {
    const state = getState()
    const activeProfile = state.user.activeProfile

    const response = await apiCallWithHost(`/v2/reference-data/all`, {}, token, activeProfile?.id)
    if (!response.ok) {
      throw new Error('Failed to fetch reference data')
    }
    return response.json()
  },
)

export const checkTranslationRequired = createAsyncThunk(
  'exercises/checkTranslationRequired',
  async ({ token, filters = {}, page = 0, size = 20 }, { getState }) => {
    const state = getState()
    const activeProfile = state.user.activeProfile

    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    })

    // Add filters if they exist
    if (filters.name) queryParams.append('name', filters.name)
    if (filters.category) queryParams.append('category', filters.category)
    if (filters.equipment) queryParams.append('equipment', filters.equipment)
    if (filters.level) queryParams.append('level', filters.level)
    if (filters.force) queryParams.append('force', filters.force)
    if (filters.mechanic) queryParams.append('mechanic', filters.mechanic)
    if (filters.muscles && filters.muscles.length > 0) {
      filters.muscles.forEach((muscle) => queryParams.append('muscles', muscle))
    }

    const response = await apiCallWithHost(
      `/v2/exercises?${queryParams.toString()}`,
      { method: 'HEAD' },
      token,
      activeProfile?.id,
    )
    if (!response.ok) {
      throw new Error('Failed to check translation requirements')
    }

    const translationRequired = response.headers.get('x-translation-required') === 'true'
    return { translationRequired }
  },
)

export const fetchExercises = createAsyncThunk(
  'exercises/fetchExercises',
  async ({ token, filters = {}, page = 0, size = 20 }, { getState }) => {
    const state = getState()
    const activeProfile = state.user.activeProfile

    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    })

    // Add filters if they exist
    if (filters.name) queryParams.append('name', filters.name)
    if (filters.category) queryParams.append('category', filters.category)
    if (filters.equipment) queryParams.append('equipment', filters.equipment)
    if (filters.level) queryParams.append('level', filters.level)
    if (filters.force) queryParams.append('force', filters.force)
    if (filters.mechanic) queryParams.append('mechanic', filters.mechanic)
    if (filters.muscles && filters.muscles.length > 0) {
      filters.muscles.forEach((muscle) => queryParams.append('muscles', muscle))
    }

    const response = await apiCallWithHost(
      `/v2/exercises?${queryParams.toString()}`,
      {},
      token,
      activeProfile?.id,
    )
    if (!response.ok) {
      throw new Error('Failed to fetch exercises')
    }
    return response.json()
  },
)

export const searchExercises = createAsyncThunk(
  'exercises/searchExercises',
  async ({ token, query, limit = 20 }, { getState }) => {
    const state = getState()
    const activeProfile = state.user.activeProfile

    const response = await apiCallWithHost(
      `/v2/exercises/search?query=${encodeURIComponent(query)}&limit=${limit}`,
      {},
      token,
      activeProfile?.id,
    )
    if (!response.ok) {
      throw new Error('Failed to search exercises')
    }
    return response.json()
  },
)

export const fetchExerciseById = createAsyncThunk(
  'exercises/fetchExerciseById',
  async ({ token, exerciseId }, { getState }) => {
    const state = getState()
    const activeProfile = state.user.activeProfile

    const response = await apiCallWithHost(
      `/v2/exercises/${exerciseId}`,
      {},
      token,
      activeProfile?.id,
    )
    if (!response.ok) {
      throw new Error('Failed to fetch exercise')
    }
    return response.json()
  },
)

const initialState = {
  exercises: [],
  referenceData: {
    categories: [],
    equipment: [],
    muscles: [],
    levels: [],
    forces: [],
    mechanics: [],
  },
  filters: {
    name: '',
    category: '',
    equipment: '',
    level: '',
    force: '',
    mechanic: '',
    muscles: [],
  },
  pagination: {
    pageNumber: 0,
    pageSize: 20,
    totalPages: 0,
    totalElements: 0,
  },
  status: 'idle', // 'idle' | 'loading' | 'translating' | 'succeeded' | 'failed'
  error: null,
  searchResults: [],
  searchStatus: 'idle',
  translationRequired: false,
  checkingTranslation: false,
}

const exercisesSlice = createSlice({
  name: 'exercises',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
      state.pagination.pageNumber = 0 // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
      state.pagination.pageNumber = 0
    },
    setPage: (state, action) => {
      state.pagination.pageNumber = action.payload
    },
    clearSearchResults: (state) => {
      state.searchResults = []
      state.searchStatus = 'idle'
    },
    addSingleExercise: (state, action) => {
      // Add a single exercise to the exercises array if it doesn't already exist
      const exercise = action.payload
      const existingIndex = state.exercises.findIndex((ex) => ex.id === exercise.id)
      if (existingIndex === -1) {
        state.exercises.push(exercise)
      } else {
        // Update existing exercise with latest data
        state.exercises[existingIndex] = exercise
      }
    },
    loadExercisesFromStorage: (state, action) => {
      // Load exercises from localStorage for a specific language
      const { exercises, referenceData } = action.payload
      state.exercises = exercises || []
      state.referenceData = { ...state.referenceData, ...referenceData }
      state.status = 'succeeded'
    },
    clearExercisesForLanguageSwitch: (state) => {
      // Clear exercises when switching languages to force re-fetch
      state.exercises = []
      state.searchResults = []
      state.status = 'idle'
      state.searchStatus = 'idle'
      state.translationRequired = false
      state.checkingTranslation = false
    },
  },
  extraReducers: (builder) => {
    builder
      // Reference data
      .addCase(fetchReferenceData.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchReferenceData.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.referenceData = action.payload
      })
      .addCase(fetchReferenceData.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      // Fetch exercises
      .addCase(fetchExercises.pending, (state) => {
        // Only set to loading if we're not already in translating state
        if (state.status !== 'translating') {
          state.status = 'loading'
        }
      })
      .addCase(fetchExercises.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const newExercises = action.payload.content || []

        // If it's the first page (page 0), replace the array
        // If it's a subsequent page, append to existing exercises
        if (action.payload.number === 0) {
          state.exercises = newExercises
        } else {
          // Append new exercises, avoiding duplicates
          newExercises.forEach((newExercise) => {
            const existingIndex = state.exercises.findIndex((ex) => ex.id === newExercise.id)
            if (existingIndex === -1) {
              state.exercises.push(newExercise)
            } else {
              // Update existing exercise with latest data
              state.exercises[existingIndex] = newExercise
            }
          })
        }

        state.pagination = {
          pageNumber: action.payload.number || 0,
          pageSize: action.payload.size || 20,
          totalPages: action.payload.totalPages || 0,
          totalElements: action.payload.totalElements || 0,
        }
      })
      .addCase(fetchExercises.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      // Search exercises
      .addCase(searchExercises.pending, (state) => {
        state.searchStatus = 'loading'
      })
      .addCase(searchExercises.fulfilled, (state, action) => {
        state.searchStatus = 'succeeded'
        state.searchResults = action.payload || []
      })
      .addCase(searchExercises.rejected, (state, action) => {
        state.searchStatus = 'failed'
        state.error = action.error.message
      })
      // Fetch exercise by ID
      .addCase(fetchExerciseById.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchExerciseById.fulfilled, (state, action) => {
        state.status = 'succeeded'
        // Add the fetched exercise to the exercises array if it's not already there
        const existingIndex = state.exercises.findIndex((ex) => ex.id === action.payload.id)
        if (existingIndex === -1) {
          state.exercises.push(action.payload)
        } else {
          state.exercises[existingIndex] = action.payload
        }
      })
      .addCase(fetchExerciseById.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      // Check translation required
      .addCase(checkTranslationRequired.pending, (state) => {
        state.checkingTranslation = true
      })
      .addCase(checkTranslationRequired.fulfilled, (state, action) => {
        state.checkingTranslation = false
        state.translationRequired = action.payload.translationRequired
        // Set status based on whether translation is required
        if (action.payload.translationRequired) {
          console.log('🌐 Translation required - setting status to translating')
          state.status = 'translating'
        } else {
          console.log('⚡ Translation not required - setting status to loading')
          state.status = 'loading'
        }
      })
      .addCase(checkTranslationRequired.rejected, (state, action) => {
        state.checkingTranslation = false
        state.translationRequired = false
        state.status = 'loading' // Fallback to regular loading
        state.error = action.error.message
      })
      // Initialize exercises from localStorage
      .addCase(initializeExercises.fulfilled, (state, action) => {
        if (action.payload.skipped) {
          // Already had exercises, nothing to do
          return
        }
        if (action.payload.cached) {
          state.exercises = action.payload.cached.exercises || []
          state.referenceData = {
            ...state.referenceData,
            ...action.payload.cached.referenceData,
          }
          state.status = 'succeeded'
        }
      })
      // Fetch missing exercises
      .addCase(fetchMissingExercises.fulfilled, (state, action) => {
        // Add all fetched exercises to the array
        action.payload.forEach((exercise) => {
          const existingIndex = state.exercises.findIndex((ex) => ex.id === exercise.id)
          if (existingIndex === -1) {
            state.exercises.push(exercise)
          } else {
            state.exercises[existingIndex] = exercise
          }
        })
      })
  },
})

export const {
  setFilters,
  clearFilters,
  setPage,
  clearSearchResults,
  addSingleExercise,
  loadExercisesFromStorage,
  clearExercisesForLanguageSwitch,
} = exercisesSlice.actions

// Selectors with error handling
export const selectExercises = (state) => state?.exercises?.exercises || []
export const selectReferenceData = (state) =>
  state?.exercises?.referenceData || {
    categories: [],
    equipment: [],
    muscles: [],
    levels: [],
    forces: [],
    mechanics: [],
  }
export const selectFilters = (state) =>
  state?.exercises?.filters || {
    name: '',
    category: '',
    equipment: '',
    level: '',
    force: '',
    mechanic: '',
    muscles: [],
  }
export const selectPagination = (state) =>
  state?.exercises?.pagination || {
    pageNumber: 0,
    pageSize: 20,
    totalPages: 0,
    totalElements: 0,
  }
export const selectExercisesStatus = (state) => state?.exercises?.status || 'idle'
export const selectExercisesError = (state) => state?.exercises?.error || null
export const selectSearchResults = (state) => state?.exercises?.searchResults || []
export const selectSearchStatus = (state) => state?.exercises?.searchStatus || 'idle'
export const selectTranslationRequired = (state) => state?.exercises?.translationRequired || false
export const selectCheckingTranslation = (state) => state?.exercises?.checkingTranslation || false

export default exercisesSlice.reducer
